import React, { useState } from 'react';
import { Lock, Zap, Database, Settings, KeyRound, SlidersHorizontal, PauseCircle, PlayCircle, XCircle } from 'lucide-react';

const SystemSettings: React.FC = () => {
    const [allowMultiLLM, setAllowMultiLLM] = useState(true);
    const [enableStreaming, setEnableStreaming] = useState(true);
    const [autoPurge, setAutoPurge] = useState(false);
    const [globalApiKey] = useState('sk-live-xyz123456...'); // obfuscated
    const [defaultRetention] = useState('90 days');
    const [env] = useState('Production');

    const [mode, setMode] = useState<'RUNNING' | 'PAUSED' | 'STOPPED'>('RUNNING');

    const modeLabel = {
        RUNNING: { label: '🟢 Running', className: 'text-green-400' },
        PAUSED: { label: '🟡 Paused', className: 'text-yellow-400' },
        STOPPED: { label: '🔴 Stopped', className: 'text-red-400' },
    };

    const handleModeChange = (newMode: typeof mode) => {
        setMode(newMode);
        alert(`System is now in ${newMode} mode.`);
        // TODO: Call backend API to persist system mode
    };

    return (
        <div className="px-6 py-8 max-w-5xl mx-auto">
            <h2 className="text-xl font-semibold text-white mb-2">System Settings</h2>
            <p className="text-sm text-gray-400 mb-6">Manage core settings for the entire agentic environment.</p>

            <div className="space-y-8 text-sm text-white">

                {/* System Info */}
                <div className="bg-neutral-900 rounded-lg border border-neutral-800 p-5">
                    <div className="flex items-center gap-2 mb-3 text-gray-400">
                        <Settings className="w-4 h-4" />
                        <h3 className="font-medium text-white text-base">System Info</h3>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-gray-300 text-sm">
                        <p><span className="text-gray-400">Environment:</span> {env}</p>
                        <p><span className="text-gray-400">Default Retention:</span> {defaultRetention}</p>
                    </div>
                </div>

                {/* Global Flags */}
                <div className="bg-neutral-900 rounded-lg border border-neutral-800 p-5">
                    <div className="flex items-center gap-2 mb-3 text-gray-400">
                        <SlidersHorizontal className="w-4 h-4" />
                        <h3 className="font-medium text-white text-base">Global Flags</h3>
                    </div>
                    <div className="space-y-4 text-sm">
                        <label className="flex items-center justify-between">
                            <span>Allow Multiple LLMs</span>
                            <input
                                type="checkbox"
                                checked={allowMultiLLM}
                                onChange={() => setAllowMultiLLM(!allowMultiLLM)}
                                className="accent-indigo-600"
                            />
                        </label>
                        <label className="flex items-center justify-between">
                            <span>Enable Streaming Responses</span>
                            <input
                                type="checkbox"
                                checked={enableStreaming}
                                onChange={() => setEnableStreaming(!enableStreaming)}
                                className="accent-indigo-600"
                            />
                        </label>
                        <label className="flex items-center justify-between">
                            <span>Auto Purge Chunks after Retention</span>
                            <input
                                type="checkbox"
                                checked={autoPurge}
                                onChange={() => setAutoPurge(!autoPurge)}
                                className="accent-red-500"
                            />
                        </label>
                    </div>
                </div>

                {/* Security & Keys */}
                <div className="bg-neutral-900 rounded-lg border border-neutral-800 p-5">
                    <div className="flex items-center gap-2 mb-3 text-gray-400">
                        <KeyRound className="w-4 h-4" />
                        <h3 className="font-medium text-white text-base">Security & Secrets</h3>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <p>
                            <span className="text-gray-400">Global API Key:</span><br />
                            <span className="font-mono text-xs">{globalApiKey}</span>
                        </p>
                        <p className="text-gray-400 text-xs italic">
                            Use Key Vault integration for better security in production.
                        </p>
                    </div>
                </div>

                {/* AI Behavior Config */}
                <div className="bg-neutral-900 rounded-lg border border-neutral-800 p-5">
                    <div className="flex items-center gap-2 mb-3 text-gray-400">
                        <Zap className="w-4 h-4" />
                        <h3 className="font-medium text-white text-base">AI Behavior Defaults</h3>
                    </div>
                    <div className="space-y-2 text-gray-300">
                        <p><span className="text-gray-400">Default Model:</span> GPT-4</p>
                        <p><span className="text-gray-400">Default Temperature:</span> 0.7</p>
                        <p><span className="text-gray-400">Fallback Model:</span> Claude 3</p>
                    </div>
                </div>

                {/* Maintenance */}
                <div className="bg-neutral-900 rounded-lg border border-neutral-800 p-5">
                    <div className="flex items-center gap-2 mb-3 text-gray-400">
                        <Database className="w-4 h-4" />
                        <h3 className="font-medium text-white text-base">System Maintenance</h3>
                    </div>
                    <button
                        className="text-red-400 hover:text-red-300 text-sm bg-red-900/30 border border-red-700 px-4 py-2 rounded"
                        onClick={() => alert('System refresh initiated.')}
                    >
                        Reindex All Vector Stores
                    </button>
                </div>
                {/* Unified Global Mode Switch */}
                <div >

                    <div className="flex flex-col ">
                        <div className="bg-neutral-900 rounded-lg border border-neutral-800 p-5">
                            <div className="flex items-center gap-2 mb-3 text-gray-400">
                                <Lock className="w-4 h-4" />
                                <h3 className="font-medium text-white text-base">Global System Mode</h3>
                            </div>

                            {/* Current Mode Display */}
                            <div className="mb-4">
                                <span className="text-sm text-gray-400">Current Mode:</span>{' '}
                                <span className={`text-sm font-medium ${modeLabel[mode].className}`}>
                                    {modeLabel[mode].label}
                                </span>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex flex-col sm:flex-row gap-3">
                                {/* <button
                                    className="flex-1 bg-green-700 hover:bg-green-800 text-white py-2 rounded shadow flex items-center justify-center gap-2"
                                    onClick={() => handleModeChange('RUNNING')}
                                >
                                    <PlayCircle className="w-4 h-4" /> Resume Operations
                                </button>
                                <button
                                    className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white py-2 rounded shadow flex items-center justify-center gap-2"
                                    onClick={() => handleModeChange('PAUSED')}
                                >
                                    <PauseCircle className="w-4 h-4" /> Pause System
                                </button> */}
                                <button
                                    className="flex-1 bg-red-700 hover:bg-red-800 text-white py-2 rounded shadow flex items-center justify-center gap-2"
                                    onClick={() => handleModeChange('STOPPED')}
                                >
                                    <XCircle className="w-4 h-4" /> Emergency Stop
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default SystemSettings;
